import { useEffect, useState } from "react";
import { admin, type AnthropicUsageBucket, type AnthropicUsageSnapshot } from "../../lib/api";

type Status = "idle" | "loading" | "ready" | "error";

function fmtUsd(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(4)}`;
}

function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toISOString().slice(0, 16).replace("T", " ");
}

/**
 * Admin "KI-Nutzung" tab (#87). 4 aggregate cards + last-50 calls table.
 * Pure read-only — no actions, no mutations. Refresh button forces a fetch.
 */
export function AdminUsageTab(): JSX.Element {
  const [status, setStatus] = useState<Status>("idle");
  const [snapshot, setSnapshot] = useState<AnthropicUsageSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setStatus("loading");
    setError(null);
    try {
      const snap = await admin.anthropicUsage.get();
      setSnapshot(snap);
      setStatus("ready");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (status === "loading" || status === "idle") {
    return <p className="text-sm text-fg-muted animate-pulse" data-testid="usage-loading">Lade Nutzungsdaten…</p>;
  }
  if (status === "error" || !snapshot) {
    return (
      <div
        role="alert"
        data-testid="usage-error"
        className="bg-danger-soft border border-danger/40 rounded-lg px-3 py-2 text-sm text-danger"
      >
        {error ?? "Konnte Nutzungsdaten nicht laden."}
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="admin-usage-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-fg-muted">
          Anthropic-API Nutzung — Tokens + geschätzte Kosten in USD.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          data-testid="usage-refresh"
          className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-fg border border-outline hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          ↻ Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <BucketCard label="Heute" bucket={snapshot.today} testid="usage-card-today" />
        <BucketCard label="Letzte 7 Tage" bucket={snapshot.last7d} testid="usage-card-7d" />
        <BucketCard label="Letzte 30 Tage" bucket={snapshot.last30d} testid="usage-card-30d" />
        <BucketCard label="Lebenszeit" bucket={snapshot.lifetime} testid="usage-card-lifetime" />
      </div>

      <section>
        <h2 className="text-xs uppercase tracking-widest text-fg-faint mb-2">
          Letzte {snapshot.recent.length} Calls
        </h2>
        {snapshot.recent.length === 0 ? (
          <p
            data-testid="usage-empty"
            className="bg-card border border-outline-soft rounded-lg px-3 py-6 text-center text-sm text-fg-muted"
          >
            Noch keine KI-Calls aufgezeichnet.
          </p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto" data-testid="usage-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-faint text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 px-2">Zeit</th>
                    <th className="text-left py-2 px-2">Vokabel</th>
                    <th className="text-right py-2 px-2">Input</th>
                    <th className="text-right py-2 px-2">Output</th>
                    <th className="text-right py-2 px-2">USD</th>
                    <th className="text-left py-2 px-2">Modell</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.recent.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-outline-soft"
                      data-testid={`usage-row-${row.id}`}
                    >
                      <td className="py-1.5 px-2 text-fg-soft font-mono text-xs">
                        {fmtTimestamp(row.ts)}
                      </td>
                      <td className="py-1.5 px-2 text-fg">{row.context ?? "—"}</td>
                      <td className="py-1.5 px-2 text-right text-fg-muted font-mono">
                        {fmtTokens(row.inputTokens)}
                      </td>
                      <td className="py-1.5 px-2 text-right text-fg-muted font-mono">
                        {fmtTokens(row.outputTokens)}
                      </td>
                      <td className="py-1.5 px-2 text-right text-fg font-mono">
                        {fmtUsd(row.estCostUsd)}
                      </td>
                      <td className="py-1.5 px-2 text-xs text-fg-faint">{row.model}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-2" data-testid="usage-cards-mobile">
              {snapshot.recent.map((row) => (
                <div
                  key={row.id}
                  className="bg-card border border-outline-soft rounded-lg px-3 py-2"
                >
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm font-semibold text-fg">{row.context ?? "—"}</span>
                    <span className="text-sm font-mono text-fg">{fmtUsd(row.estCostUsd)}</span>
                  </div>
                  <div className="text-[11px] text-fg-faint flex gap-2 mt-1">
                    <span>{fmtTimestamp(row.ts)}</span>
                    <span>·</span>
                    <span>
                      {fmtTokens(row.inputTokens)} in · {fmtTokens(row.outputTokens)} out
                    </span>
                    <span>·</span>
                    <span>{row.model}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </section>
  );
}

function BucketCard({
  label,
  bucket,
  testid,
}: {
  label: string;
  bucket: AnthropicUsageBucket;
  testid: string;
}) {
  return (
    <div
      className="bg-card border border-outline-soft rounded-lg px-3 py-2.5 space-y-0.5"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-fg-faint">{label}</div>
      <div className="text-2xl font-bold font-mono text-fg">{fmtUsd(bucket.estCostUsd)}</div>
      <div className="text-[11px] text-fg-muted font-mono">
        {fmtTokens(bucket.inputTokens + bucket.outputTokens)} Tokens · {bucket.callCount} Calls
      </div>
    </div>
  );
}
